/**
 * Which graduating class a product prints on this student -- the "2027" in
 * "CLASS OF 2027" on the graduate magnet.
 *
 * ONE rule, asked by both renderers of that magnet (the gifts page's `Item` and
 * the cart's `DisplayableSheet`), so the shelf, the product screen and the cart
 * can never disagree about the year a family is about to buy.
 *
 * Two sources, in this order:
 *
 *   1. `student.gradYear` -- served by Api::StudentResource from
 *      Student#grad_year, which is roster grade + the school-year calendar.
 *      It is also what EC2 substitutes for {{grad_year}}, so a preview built on
 *      it says what a rendered piece would say.
 *   2. a pose's `folder` ("seniors2027") -- the sitting the photo came from,
 *      which is how the senior builder scopes its "Class of YYYY" overlays. Here
 *      only as the fallback for a student whose grade is not a number ("ST",
 *      "PS", blank): the gifts page loads poses with their folder; the cart does
 *      not, and simply passes none.
 *
 * ⚠️ `null` IS A REAL ANSWER. A student with no numeric grade and no senior
 * folder has no class year we can vouch for, and the caller must then draw the
 * magnet WITHOUT the "CLASS OF" line rather than a guess or a placeholder --
 * the house rule for every {{token}} on this page (see GiftHero's fillTemplate).
 */
const SEASON_YEAR = /(\d{4})/;

function yearFromSeniorFolder(folder) {
  const value = String(folder || '').toLowerCase();
  if (!value.startsWith('seniors')) return null;

  const match = value.match(SEASON_YEAR);
  return match ? Number(match[1]) : null;
}

export default function classYearFor({ student = null, poses = [] } = {}) {
  const fromRoster = Number(student?.gradYear);
  if (Number.isInteger(fromRoster) && fromRoster > 0) return fromRoster;

  const fromFolder = (poses || [])
    .map((pose) => yearFromSeniorFolder(pose?.folder))
    .find((year) => year !== null);

  return fromFolder ?? null;
}
